import type { HTMLAttributes } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import { PLAN_COLORS } from '../constants';
import type { PlanConfig, Segment } from '../types';

interface SegmentCardProps {
  segment: Segment;
  index: number;
  config: PlanConfig;
  canDelete: boolean;
  /** When true, shows the delete icon even if {@link canDelete} is false (disabled state). */
  showDeleteIcon?: boolean;
  onNameChange: (idx: number, name: string) => void;
  onPctChange: (idx: number, value: number) => void;
  onStartDayChange: (idx: number, value: number) => void;
  /** Opens delete confirmation (parent handles actual delete). */
  onRequestDelete: (idx: number) => void;
  /** Dealer primary segments selectable for this card (excludes names used elsewhere). */
  nameOptions: string[];
  /** Spread onto the drag handle (from useSortable listeners/attributes). */
  dragHandleProps?: HTMLAttributes<HTMLElement>;
}

const statBox = (value: string | number, label: string, color: string) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography sx={{ fontSize: 11, fontWeight: 600, fontFamily: 'monospace', color }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: '1px' }}>{label}</Typography>
  </Box>
);

export const SegmentCard = ({
  segment,
  index,
  config,
  canDelete,
  showDeleteIcon,
  onNameChange,
  onPctChange,
  onStartDayChange,
  onRequestDelete,
  nameOptions,
  dragHandleProps,
}: SegmentCardProps) => {
  const total = config.total;
  const ssBpm = Math.round(30 / config.ssCad);
  const ssLpb = Math.max(1, Math.round(config.monthly / ssBpm));
  const count = Math.max(1, Math.round((segment.pct / 100) * total));
  const segFrac = segment.pct / 100;
  const newMo = Math.max(1, Math.round(config.monthly * segFrac));
  const newBatch = Math.max(1, Math.round(ssLpb * segFrac));

  return (
    <Box
      sx={{
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.25,
        mb: 1,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            component="span"
            {...dragHandleProps}
            aria-label="Drag to reorder segment"
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'grab',
              color: 'text.disabled',
              touchAction: 'none',
              '&:active': { cursor: 'grabbing' },
              '&:hover': { color: 'text.secondary' },
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box
            sx={{
              fontSize: 10,
              fontWeight: 700,
              fontFamily: 'monospace',
              color: PLAN_COLORS.orange,
              bgcolor: PLAN_COLORS.orangeBg,
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
            }}
          >
            #{segment.order}
          </Box>
        </Box>
        {(showDeleteIcon ?? canDelete) && (
          <IconButton
            size="small"
            disabled={!canDelete}
            onClick={() => onRequestDelete(index)}
            sx={{
              p: 0.25,
              color: 'text.secondary',
              '&:hover': { color: canDelete ? PLAN_COLORS.red : undefined },
            }}
            aria-label="Delete segment"
            title={canDelete ? 'Delete segment' : 'Segment delete is temporarily disabled'}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>

      {nameOptions.length > 1 ? (
        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
          <Select
            value={segment.name}
            onChange={(e) => onNameChange(index, e.target.value)}
            displayEmpty
            inputProps={{ 'aria-label': 'Segment name' }}
            sx={{
              fontSize: 13,
              fontWeight: 600,
              '& .MuiSelect-select': { py: 0.75 },
            }}
          >
            {nameOptions.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            mb: 1,
            py: 0.5,
            color: 'text.primary',
          }}
          aria-label="Segment name"
        >
          {segment.name || '—'}
        </Typography>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          gap: 1,
          alignItems: 'end',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 10, color: 'text.secondary', mb: 0.5 }}>% of pool</Typography>
          <TextField
            type="number"
            size="small"
            value={segment.pct}
            onChange={(e) => onPctChange(index, parseFloat(e.target.value))}
            inputProps={{ min: 1, max: 99, step: 1, 'aria-label': 'Percent of pool' }}
            sx={{
              width: 72,
              '& .MuiOutlinedInput-input': {
                py: 0.5,
                fontSize: 12,
                fontFamily: 'monospace',
                fontWeight: 600,
                color: PLAN_COLORS.orange,
              },
              '& .MuiOutlinedInput-root': {
                bgcolor: PLAN_COLORS.orangeBg,
                '& fieldset': { borderColor: PLAN_COLORS.orange },
              },
            }}
          />
        </Box>
        <Box sx={{ pb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, segment.pct)}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': { bgcolor: PLAN_COLORS.orange },
            }}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10, color: 'text.secondary', mb: 0.5 }}>
            Load from day
          </Typography>
          <TextField
            type="number"
            size="small"
            value={segment.startDay}
            onChange={(e) => onStartDayChange(index, parseInt(e.target.value, 10))}
            inputProps={{ min: 1, 'aria-label': 'Load from day' }}
            sx={{
              width: 72,
              '& .MuiOutlinedInput-input': { py: 0.5, fontSize: 12, fontFamily: 'monospace' },
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 0.5,
          mt: 0.75,
          pt: 0.75,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        {statBox(count.toLocaleString(), 'Past leads', PLAN_COLORS.past)}
        {statBox(newMo, 'New/month', PLAN_COLORS.newLeads)}
        {statBox(newBatch, 'New/batch', PLAN_COLORS.newBatch)}
      </Box>
    </Box>
  );
};
